import ForceGraph3D from 'react-force-graph-3d';
import * as THREE from 'three';
import { useRef, useEffect } from 'react';
import { nodeColorFunction } from '../utils/colorUtils';

// Define a global variable to control opacity adjustment
let adjustOpacity = true;

function ForceGraph({ data, setSelectedNode, nodeSize, colorKey }) {
  const graphRef = useRef();

  let maxNodeDistance = 0;
  let minNodeDistance = Infinity;
  let currentCameraPosition = null;

  useEffect(() => {
    if (graphRef.current) {
      const nodeCount = data.nodes.length;
      const zoomFactor = 100;
      const cameraZoomOutPosition = { x: 0, y: 0, z: Math.max(2500, nodeCount * zoomFactor) };
      const cameraFocusPoint = { x: 0, y: 0, z: 0 };
      const transitionDuration = 1000;

      graphRef.current.cameraPosition(cameraZoomOutPosition, cameraFocusPoint, transitionDuration);

      // Auto zoom to fit the graph
      graphRef.current.zoomToFit(transitionDuration, 100); // 100 is the padding around the graph
    }
  }, [data]);

  useEffect(() => {
    if (graphRef.current) {
      const axesHelper = new THREE.AxesHelper(500); // Create an axes helper with a size of 500
      graphRef.current.scene().add(axesHelper); // Add the axes to the graph's scene

      return () => {
        // Cleanup the axes when the component unmounts
        graphRef.current.scene().remove(axesHelper);
        maxNodeDistance = 0;
        minNodeDistance = Infinity;
      };
    }
  }, []);

  const createCustomNode = (node) => {
    // Create a sphere geometry for the node
    const geometry = new THREE.SphereGeometry(nodeSize, 16, 16);
  
    // Use a material with a base color determined by `nodeColorFunction`
    const material = new THREE.MeshStandardMaterial({
      color: nodeColorFunction(colorKey)(node),
      transparent: true,
      opacity: 1,
    });
  
    const sphere = new THREE.Mesh(geometry, material);
  
    // Conditionally adjust opacity based on the global variable
    sphere.onBeforeRender = (renderer, scene, camera) => {
      if (adjustOpacity) {
        const distanceToCamera = sphere.position.distanceTo(camera.position);
  
        // Update min and max distances
        maxNodeDistance = distanceToCamera > maxNodeDistance ? distanceToCamera : maxNodeDistance;
        minNodeDistance = distanceToCamera < minNodeDistance ? distanceToCamera : minNodeDistance;
  
        // Calculate opacity factor
        const range = maxNodeDistance - minNodeDistance || 1; // Avoid division by 0
        const factor = (distanceToCamera - minNodeDistance) / range;
  
        // Map factor to opacity range (100% for closest, 0% for furthest)
        const minOpacity = 0.1;
        const maxOpacity = 1.0;
        material.opacity = maxOpacity - ((Math.pow(factor, 2)) * (maxOpacity - minOpacity));
      }
    };

    return sphere;
  };


  return (
    <ForceGraph3D
      ref={graphRef}
      graphData={data}
      onNodeClick={node => setSelectedNode(node)} // Handle node click
      nodeRelSize={nodeSize} // Use dynamic node size
      nodeThreeObject={createCustomNode} // Custom 3D nodes
      enableNodeDrag={false}
      d3AlphaDecay={0.4}
      forceEngine="none"
      charge={0}
    />
  );
}

export default ForceGraph;
